import { type MutableRefObject, useCallback, useEffect, useRef } from 'react';
import type { ImprUser } from '../../../../api/types';
import type { AppError } from '../../../../errorHandler/errors';
import { useIdpUsers } from './useIdpUsers';

interface Args {
  lastElementRef: MutableRefObject<HTMLDivElement | null>;
}

type IdpUsersInfiniteScrollType = {
  data?: ImprUser[];
  isFetchingNextPage: boolean;
  refetch: () => void;
  isLoading: boolean;
  hasNextPage: boolean;
  error?: AppError;
};

const useIdpInfiniteScroll = ({ lastElementRef }: Args): IdpUsersInfiniteScrollType => {
  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, refetch, isLoading, error } =
    useIdpUsers();

  const observerElem = useRef<IntersectionObserver | null>(null);

  const observerCallback = useCallback(
    (entries: IntersectionObserverEntry[]) => {
      const [entry] = entries;
      if (entry.isIntersecting && hasNextPage && !isFetchingNextPage) {
        void fetchNextPage();
      }
    },
    [fetchNextPage, hasNextPage, isFetchingNextPage],
  );

  const observer = useCallback(
    (node: HTMLDivElement) => {
      if (observerElem.current) {
        observerElem.current.disconnect();
      }

      observerElem.current = new IntersectionObserver(observerCallback, { rootMargin: '400px' });
      if (node) {
        observerElem.current.observe(node);
      }
    },
    [observerCallback],
  );

  useEffect(() => {
    const currentElement = lastElementRef.current;
    if (currentElement) {
      observer(currentElement);
    }
    return () => {
      observerElem.current?.disconnect();
      observerElem.current = null;
    };
  }, [lastElementRef, observer]);

  return { data, isFetchingNextPage, refetch, isLoading, hasNextPage, error: error as AppError };
};

export default useIdpInfiniteScroll;
